import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Forum.css';
import api from '../../services/api';
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import { emitToast } from '../../utils/toast';

const Annonces = () => {
  const navigate = useNavigate();
  const [annonces, setAnnonces] = useState({ vends: [], recherche: [], services: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [myAds, setMyAds] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAd, setEditAd] = useState({ id: '', type: 'vends', titre: '', description: '', prix: '', imageUrl: '', images: [] });

  const [showModal, setShowModal] = useState(false);
  const [newAnnonce, setNewAnnonce] = useState({ type: 'vends', titre: '', description: '', prix: '', imageUrl: '', images: [] });
  const hlDoneRef = useRef(false);
  const API_BASE = (api.defaults.baseURL || process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
  const newImageInputRef = useRef(null);
  const editImageInputRef = useRef(null);

  const toAbsoluteMediaUrl = (url = '') => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    const fixedProto = raw.replace(/^https\//i, 'https://').replace(/^http\//i, 'http://');
    if (/^https?:\/\//i.test(fixedProto)) return fixedProto;
    if (fixedProto.startsWith('/')) return `${API_BASE}${fixedProto}`;
    return `${API_BASE}/${fixedProto}`;
  };

  // Charger depuis l'API
  const fetchAds = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/api/forum/ads?status=approved&limit=50');
      const list = Array.isArray(res?.data) ? res.data : [];
      const vends = list.filter(a => a.type === 'vends').map(a => ({ id: a.id, titre: a.title, description: a.description, prix: a.price || '—', imageUrl: a.imageUrl || '' }));
      const recherche = list.filter(a => a.type === 'recherche').map(a => ({ id: a.id, titre: a.title, description: a.description, imageUrl: a.imageUrl || '' }));
      const services = list.filter(a => a.type === 'services').map(a => ({ id: a.id, titre: a.title, description: a.description, imageUrl: a.imageUrl || '' }));
      setAnnonces({ vends, recherche, services });
    } catch (e) {
      setError("Impossible de charger les annonces.");
      setAnnonces({ vends: [], recherche: [], services: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAds(); }, []);

  // Charger mes annonces si connecté
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // seulement si connecté
    fetchMyAds();
  }, []);

  const fetchMyAds = async () => {
    try {
      setMyLoading(true);
      setMyError('');
      const r = await api.get('/api/forum/ads/mine');
      setMyAds(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      setMyError("Impossible de charger vos annonces.");
    } finally {
      setMyLoading(false);
    }
  };

  // Applique un surlignage si on vient de l'admin avec hlType=ad&hlId=
  useEffect(() => {
    if (hlDoneRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('hlType') === 'ad') {
      const id = params.get('hlId');
      if (id) {
        // attendre que la liste soit rendue
        const el = document.getElementById(`ad-${id}`);
        if (el) {
          hlDoneRef.current = true;
          el.classList.add('highlight');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => el.classList.remove('highlight'), 4000);
        }
      }
    }
  }, [annonces]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');
    try {
      const payload = {
        type: newAnnonce.type,
        title: newAnnonce.titre.trim(),
        description: newAnnonce.description.trim(),
        price: newAnnonce.type === 'vends' ? (newAnnonce.prix || '') : '',
        imageUrl: newAnnonce.imageUrl || '',
        images: Array.isArray(newAnnonce.images) ? newAnnonce.images : []
      };
      await api.post('/api/forum/ads', payload);
      setShowModal(false);
      setNewAnnonce({ type: 'vends', titre: '', description: '', prix: '', imageUrl: '', images: [] });
      await fetchAds();
      if (localStorage.getItem('token')) { await fetchMyAds(); }
      emitToast("Annonce envoyée pour approbation par un administrateur.");
    } catch (err) {
      emitToast("Publication impossible (connexion requise).");
    }
  };

  // Upload image helper
  const uploadImage = async (file) => {
    // Validation type/poids
    if (!file.type.startsWith('image/')) throw new Error('Type de fichier non supporté');
    const max = 4 * 1024 * 1024; // 4MB
    if (file.size > max) throw new Error('Image trop volumineuse (>4MB)');
    const form = new FormData();
    form.append('media', file);
    form.append('title', file.name);
    const res = await api.post('/api/media', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    const created = res?.data?.media;
    return created?.url || '';
  };

  const handleNewImageSelected = async (e) => {
    const files = (e.target.files && Array.from(e.target.files)) || [];
    if (files.length === 0) return;
    try {
      const urls = [];
      for (const f of files) {
        try { const u = await uploadImage(f); if (u) urls.push(u); } catch (err) { emitToast(err.message || "Échec d'upload pour " + f.name); }
      }
      if (urls.length) {
        setNewAnnonce(prev => {
          const nextImages = [...(prev.images||[]), ...urls];
          return { ...prev, images: nextImages, imageUrl: prev.imageUrl || nextImages[0] };
        });
      }
    } finally {
      e.target.value = '';
    }
  };

  const handleEditImageSelected = async (e) => {
    const files = (e.target.files && Array.from(e.target.files)) || [];
    if (!files.length) return;
    try {
      const urls = [];
      for (const f of files) {
        try { const u = await uploadImage(f); if (u) urls.push(u); } catch (err) { emitToast(err.message || "Échec d'upload pour " + f.name); }
      }
      if (urls.length) setEditAd(prev => {
        const nextImages = [...(prev.images||[]), ...urls];
        return { ...prev, images: nextImages, imageUrl: prev.imageUrl || nextImages[0] };
      });
    } finally {
      e.target.value = '';
    }
  };

  const removeNewImageAt = (idx) => {
    setNewAnnonce(prev => {
      const arr = [...(prev.images||[])];
      if (idx>=0 && idx<arr.length) arr.splice(idx,1);
      return { ...prev, images: arr, imageUrl: arr[0] || prev.imageUrl };
    });
  };

  const removeEditImageAt = (idx) => {
    setEditAd(prev => {
      const arr = [...(prev.images||[])];
      if (idx>=0 && idx<arr.length) arr.splice(idx,1);
      return { ...prev, images: arr, imageUrl: arr[0] || prev.imageUrl };
    });
  };

  const handleReport = async (ad) => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');
    const reason = window.prompt('Raison du signalement (ex: spam, offensif, inexact, autre)', 'autre');
    if (!reason) return;
    try {
      await api.post('/api/forum/reports', { targetType: 'ad', targetId: ad.id, reason });
      emitToast('Signalement envoyé. Merci.');
    } catch (e) {
      emitToast('Signalement impossible.');
    }
  };

  return (
    <section className="annonces-section">
      <h2>Petites Annonces</h2>
      {/* Mes annonces (si connecté) */}
      {localStorage.getItem('token') && (
        <div className="annonce-category" style={{ marginBottom: '1.5rem' }}>
          <h3><i className="fas fa-user" aria-hidden="true"></i> Mes annonces</h3>
          {myLoading && <div>Chargement...</div>}
          {!myLoading && myError && <div className="forum-error">{myError}</div>}
          {!myLoading && !myError && myAds.length === 0 && (
            <div>Aucune annonce pour le moment.</div>
          )}
          {!myLoading && !myError && myAds.length > 0 && (
            <div className="my-ads-list">
              {myAds.map(ad => (
                <div key={ad.id} className="my-ad-item">
                  <div className="my-ad-main">
                    <div className="my-ad-title">
                      <span className="my-ad-type">{(ad.type || '').toUpperCase()}</span>
                      <span className="my-ad-dot" aria-hidden="true">•</span>
                      <span className="my-ad-name">{ad.title}</span>
                    </div>
                    <div className="my-ad-meta">
                      {ad.status && <span className={`my-ad-status is-${ad.status}`}>{ad.status}</span>}
                      {ad.createdAt && <span className="my-ad-date">{new Date(ad.createdAt).toLocaleDateString('fr-FR')}</span>}
                      {ad.price && <span className="my-ad-price">{ad.price}</span>}
                    </div>
                    {ad.description && <div className="my-ad-desc">{ad.description}</div>}
                  </div>
                  <div className="my-ad-actions">
                    <button className="link-like" onClick={() => {
                      setEditAd({ id: ad.id, type: ad.type, titre: ad.title, description: ad.description || '', prix: ad.price || '', imageUrl: ad.imageUrl || '', images: Array.isArray(ad.images)?ad.images:[] });
                      setShowEditModal(true);
                    }}>Modifier</button>
                    <button className="link-like" onClick={async () => {
                      if (!window.confirm('Supprimer cette annonce ?')) return;
                      try {
                        await api.delete(`/api/forum/ads/mine/${ad.id}`);
                        await fetchMyAds();
                        await fetchAds();
                        emitToast('Annonce supprimée');
                      } catch (e) { emitToast('Suppression impossible.'); }
                    }}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="annonces-grid">
        <div className="annonce-category">
          <h3><i className="fas fa-tag" aria-hidden="true"></i> Vends</h3>
          {loading && <div>Chargement...</div>}
          {!loading && error && <div className="forum-error">{error}</div>}
          {!loading && !error && (
            annonces.vends.length === 0 ? (
              <div>Aucune annonce pour l'instant. <button className="link-like" onClick={() => setShowModal(true)}>Publier une annonce</button></div>
            ) : (
              <div className="ad-list">
                {annonces.vends.map((item, idx) => (
                  <AnimatedSection key={item.id} delay={idx % 4} animation="fade-up">
                    <div className="ad-card" id={`ad-${item.id}`}>
                    {item.imageUrl && (
                      <img
                        className="ad-thumb"
                        src={toAbsoluteMediaUrl(item.imageUrl)}
                        alt={item.titre}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="300"
                      />
                    )}
                    <div className="ad-card-header">
                      <span className="ad-title">{item.titre}</span>
                      {item.prix && <span className="ad-price">{item.prix}</span>}
                    </div>
                    {item.description && (<p className="ad-desc">{item.description}</p>)}
                    <div className="ad-actions">
                      <button className="link-like" onClick={() => handleReport(item)}>Signaler</button>
                    </div>
                  </div>
                  </AnimatedSection>
                ))}
              </div>
            )
          )}
        </div>

        <div className="annonce-category">
          <h3><i className="fas fa-search" aria-hidden="true"></i> Recherche</h3>
          {loading && <div>Chargement...</div>}
          {!loading && !error && (
            annonces.recherche.length === 0 ? (
              <div>Aucune annonce de recherche. <button className="link-like" onClick={() => setShowModal(true)}>Publier une annonce</button></div>
            ) : (
              <div className="ad-list">
                {annonces.recherche.map((item, idx) => (
                  <AnimatedSection key={item.id} delay={idx % 4} animation="fade-up">
                    <div className="ad-card" id={`ad-${item.id}`}>
                    {item.imageUrl && (
                      <img
                        className="ad-thumb"
                        src={toAbsoluteMediaUrl(item.imageUrl)}
                        alt={item.titre}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="300"
                      />
                    )}
                    <div className="ad-card-header">
                      <span className="ad-title">{item.titre}</span>
                    </div>
                    {item.description && (<p className="ad-desc">{item.description}</p>)}
                    <div className="ad-actions">
                      <button className="link-like" onClick={() => handleReport(item)}>Signaler</button>
                    </div>
                  </div>
                  </AnimatedSection>
                ))}
              </div>
            )
          )}
        </div>

        <div className="annonce-category">
          <h3><i className="fas fa-hands-helping" aria-hidden="true"></i> Services</h3>
          {loading && <div>Chargement...</div>}
          {!loading && !error && (
            annonces.services.length === 0 ? (
              <div>Aucun service publié. <button className="link-like" onClick={() => setShowModal(true)}>Publier une annonce</button></div>
            ) : (
              <div className="ad-list">
                {annonces.services.map((item, idx) => (
                  <AnimatedSection key={item.id} delay={idx % 4} animation="fade-up">
                    <div className="ad-card" id={`ad-${item.id}`}>
                    {item.imageUrl && (
                      <img
                        className="ad-thumb"
                        src={toAbsoluteMediaUrl(item.imageUrl)}
                        alt={item.titre}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="300"
                      />
                    )}
                    <div className="ad-card-header">
                      <span className="ad-title">{item.titre}</span>
                    </div>
                    {item.description && (<p className="ad-desc">{item.description}</p>)}
                    <div className="ad-actions">
                      <button className="link-like" onClick={() => handleReport(item)}>Signaler</button>
                    </div>
                  </div>
                  </AnimatedSection>
                ))}
              </div>
            )
          )}
        </div>
      </div>
      <button className="new-annonce-btn" onClick={() => setShowModal(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Publier une annonce
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="new-discussion-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Publier une annonce</h2>
            <form className="new-discussion-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Type</label>
                <select
                  value={newAnnonce.type}
                  onChange={(e) => setNewAnnonce({ ...newAnnonce, type: e.target.value })}
                >
                  <option value="vends">Vends</option>
                  <option value="recherche">Recherche</option>
                  <option value="services">Services</option>
                </select>
              </div>
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  placeholder="Ex: Table basse en bois"
                  value={newAnnonce.titre}
                  onChange={(e) => setNewAnnonce({ ...newAnnonce, titre: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Quelques détails utiles..."
                  rows="4"
                  value={newAnnonce.description}
                  onChange={(e) => setNewAnnonce({ ...newAnnonce, description: e.target.value })}
                  required
                ></textarea>
              </div>
              {newAnnonce.type === 'vends' && (
                <div className="form-group">
                  <label>Prix (optionnel)</label>
                  <input
                    type="text"
                    placeholder="Ex: 50€"
                    value={newAnnonce.prix}
                    onChange={(e) => setNewAnnonce({ ...newAnnonce, prix: e.target.value })}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Image (optionnel)</label>
                {Array.isArray(newAnnonce.images) && newAnnonce.images.length>0 && (
                  <div className="ad-thumbs">
                    {newAnnonce.images.map((u,idx)=> (
                      <div className="ad-thumb-mini" key={idx}>
                        <img src={toAbsoluteMediaUrl(u)} alt={`Illustration ${idx + 1}`} />
                        <button type="button" className="remove-img" onClick={()=>removeNewImageAt(idx)} aria-label={`Supprimer l’illustration ${idx + 1}`}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button type="button" className="btn-submit" onClick={() => newImageInputRef.current && newImageInputRef.current.click()}>Ajouter image(s)</button>
                </div>
                <input ref={newImageInputRef} type="file" accept="image/*" multiple onChange={handleNewImageSelected} style={{ display:'none' }} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">Publier</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Modifier mon annonce</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const token = localStorage.getItem('token');
              if (!token) return navigate('/login');
              try {
                const payload = {
                  type: editAd.type,
                  title: editAd.titre.trim(),
                  description: editAd.description.trim(),
                  price: editAd.type === 'vends' ? (editAd.prix || '') : '',
                  imageUrl: editAd.imageUrl || '',
                  images: Array.isArray(editAd.images) ? editAd.images : []
                };
                await api.put(`/api/forum/ads/mine/${editAd.id}`, payload);
                setShowEditModal(false);
                await fetchMyAds();
                await fetchAds();
                emitToast("Modifications enregistrées (l'annonce repasse en attente d'approbation).");
              } catch (err) {
                emitToast('Mise à jour impossible.');
              }
            }}>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-input" value={editAd.type} onChange={(e) => setEditAd(prev => ({ ...prev, type: e.target.value }))}>
                  <option value="vends">Vends</option>
                  <option value="recherche">Recherche</option>
                  <option value="services">Services</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Titre</label>
                <input className="form-input" value={editAd.titre} onChange={(e) => setEditAd(prev => ({ ...prev, titre: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea rows="3" className="form-input" value={editAd.description} onChange={(e) => setEditAd(prev => ({ ...prev, description: e.target.value }))} required />
              </div>
              {editAd.type === 'vends' && (
                <div className="form-group">
                  <label className="form-label">Prix</label>
                  <input className="form-input" value={editAd.prix} onChange={(e) => setEditAd(prev => ({ ...prev, prix: e.target.value }))} placeholder="Ex: 50€" />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Image (optionnel)</label>
                {Array.isArray(editAd.images) && editAd.images.length>0 && (
                  <div className="ad-thumbs">
                    {editAd.images.map((u,idx)=> (
                      <div className="ad-thumb-mini" key={idx}>
                        <img src={toAbsoluteMediaUrl(u)} alt={`Illustration ${idx + 1}`} />
                        <button type="button" className="remove-img" onClick={()=>removeEditImageAt(idx)} aria-label={`Supprimer l’illustration ${idx + 1}`}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button type="button" className="btn-submit" onClick={() => editImageInputRef.current && editImageInputRef.current.click()}>Ajouter image(s)</button>
                </div>
                <input ref={editImageInputRef} type="file" accept="image/*" multiple onChange={handleEditImageSelected} style={{ display:'none' }} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Annuler</button>
                <button type="submit" className="btn-submit">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default Annonces;
